'use client'

import * as React from 'react'

import { usePagination, type PaginationItemType } from '../../../../hooks/use-pagination'
import { PaginationItem } from '../PaginationItem/PaginationItem'
import PaginationPerPage from '../PaginationItemsPerPage/PaginationPerPage'

export interface Props {
  boundaryCount?: number
  count: number
  defaultPage?: number
  disabled?: boolean
  hideNextButton?: boolean
  hidePrevButton?: boolean
  onChange: (event: React.MouseEvent, value: number | null, itemPerPage?: number) => void
  onPerPageChange: (itemsPerPage: number) => void
  page?: number
  showFirstButton?: boolean
  showLastButton?: boolean
  siblingCount?: number
  perPage: number
  perPageOptions: Array<number>
  hidePerPage?: boolean
}

// Adapted from MUI's Pagination component:
// https://mui.com/material-ui/react-pagination/
const PaginationWrapper = ({
  boundaryCount,
  count,
  defaultPage,
  disabled,
  hideNextButton,
  hidePrevButton,
  onChange,
  onPerPageChange,
  page,
  showFirstButton,
  showLastButton,
  siblingCount,
  perPage,
  perPageOptions,
  hidePerPage
}: Props) => {
  const { items } = usePagination({
    boundaryCount,
    count,
    defaultPage,
    disabled,
    hideNextButton,
    hidePrevButton,
    onChange,
    page,
    showFirstButton,
    showLastButton,
    siblingCount
  })

  return (
    <div className="flex flex-row items-center justify-center gap-10">
      <ul
        aria-label="pagination navigation"
        className="m-0 flex list-none flex-row flex-wrap items-center gap-2 p-0"
      >
        {items.map((item): React.JSX.Element => {
          const key = item.type === 'page' ? `page-${item.page}` : item.type

          return (
            <li key={key}>
              <PaginationItem
                aria-label={defaultGetAriaLabel(item.type, item.page, item.selected)}
                {...item}
              />
            </li>
          )
        })}
      </ul>
      <PaginationPerPage
        perPage={perPage}
        options={perPageOptions}
        onChange={onPerPageChange}
        disabled={false}
        hidden={hidePerPage}
      />
    </div>
  )
}

const defaultGetAriaLabel = (
  type: PaginationItemType,
  page: number | null,
  selected?: boolean
): string => {
  if (type === 'page') {
    return `${selected ? '' : 'Go to '}page ${page}`
  }

  return `Go to ${type} page`
}

export default PaginationWrapper
